<template>
  <div
    class="rounded-xl border border-gray-200 bg-white shadow-sm dark:border-gray-700 dark:bg-gray-800"
  >
    <!-- Header Section -->
    <div
      class="flex cursor-pointer items-center justify-between p-5"
      @click="emailExpanded = !emailExpanded"
    >
      <div class="flex items-center gap-4">
        <div
          class="flex h-12 w-12 items-center justify-center rounded-lg bg-purple-100 dark:bg-purple-900/30"
        >
          <i
            class="fa-solid fa-envelope text-2xl text-purple-600 dark:text-purple-400"
          ></i>
        </div>
        <div>
          <h3 class="text-lg font-medium text-gray-800 dark:text-white">
            Email Marketing
          </h3>
          <div class="flex items-center gap-2 mt-1">
            <span
              class="inline-flex h-2 w-2 rounded-full"
              :class="isEmailConnected ? 'bg-green-500' : 'bg-gray-400'"
            ></span>
            <span
              class="text-sm font-medium"
              :class="
                isEmailConnected
                  ? 'text-green-600 dark:text-green-400'
                  : 'text-gray-500 dark:text-gray-400'
              "
            >
              {{ isEmailConnected ? "Connected" : "Not Connected" }}
            </span>
          </div>
        </div>
      </div>

      <div class="flex items-center gap-3">
        <div class="flex items-center">
          <div
            style="cursor: pointer"
            class="relative inline-block w-8 h-6 rounded-full"
          >
            <input
              :disabled="!isEmailConnected"
              id="emailAutoPilotSwitch"
              style="cursor: pointer"
              type="checkbox"
              v-model="emailAutoPilotEnabled"
              class="absolute w-8 h-6 transition-colors duration-300 rounded-full appearance-none peer bg-blue-gray-100 checked:bg-main-900 peer-checked:border-main-900 peer-checked:before:bg-main-900 dark:checked:bg-sub-900 dark:peer-checked:border-sub-900 dark:peer-checked:before:bg-sub-900"
            />
            <label
              htmlFor="emailAutoPilotSwitch"
              style="cursor: pointer"
              class="before:content[''] absolute top-2/4 -left-2 h-6 w-6 -translate-y-2/4 rounded-full border border-blue-gray-100 bg-white shadow-md transition-all duration-300 before:absolute before:top-2/4 before:left-2/4 before:block before:h-10 before:w-10 before:-translate-y-2/4 before:-translate-x-2/4 before:rounded-full before:bg-blue-gray-500 before:opacity-0 before:transition-opacity hover:before:opacity-10 peer-checked:translate-x-full peer-checked:border-main-900 peer-checked:before:bg-main-900"
            >
              <div
                class="inline-block p-5 rounded-full top-2/4 left-2/4 -translate-x-2/4 -translate-y-2/4"
                data-ripple-dark="true"
              ></div>
            </label>
          </div>
          <span
            class="ml-5 text-sm font-medium text-gray-700 dark:text-gray-300"
            >Auto Pilot</span
          >
        </div>
        <button
          class="text-gray-500 transition-colors hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
        >
          <i
            class="fas"
            :class="emailExpanded ? 'fa-chevron-up' : 'fa-chevron-down'"
          ></i>
        </button>
      </div>
    </div>

    <!-- Connection Button (when collapsed) -->
    <div v-if="!emailExpanded && !isEmailConnected" class="px-5 pb-5">
      <button
        @click.stop="connectEmail"
        class="w-full rounded-lg bg-purple-600 px-4 py-2 text-white transition-colors hover:bg-purple-700 dark:bg-purple-700 dark:hover:bg-purple-800"
      >
        Connect Email Service
      </button>
    </div>

    <!-- Expanded Content -->
    <div
      v-if="emailExpanded"
      class="space-y-6 border-t border-gray-200 p-6 dark:border-gray-600"
    >
      <div v-if="!isEmailConnected">
        <p class="mb-4 dark:text-gray-300">
          Connect your email service provider for email campaigns.
        </p>

        <!-- Warning about Gmail limitations -->
        <div
          class="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-4 dark:bg-yellow-900/20 dark:border-yellow-500"
        >
          <div class="flex">
            <div class="flex-shrink-0">
              <svg
                class="h-5 w-5 text-yellow-400 dark:text-yellow-300"
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fill-rule="evenodd"
                  d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                  clip-rule="evenodd"
                />
              </svg>
            </div>
            <div class="ml-3">
              <p class="text-sm text-yellow-700 dark:text-yellow-300">
                <strong>Important:</strong> Gmail/Google Workspace has strict
                sending limits (500 emails/day for free accounts). For bulk
                email campaigns, we recommend using a dedicated email service
                provider.
              </p>
            </div>
          </div>
        </div>

        <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <!-- Mailchimp -->
          <button
            @click="connectEmail('mailchimp')"
            class="flex items-center justify-center gap-2 rounded-lg bg-blue-600 px-4 py-3 font-medium text-white transition-colors hover:bg-blue-700 dark:bg-blue-700 dark:hover:bg-blue-800"
          >
            <i class="fa-brands fa-mailchimp"></i> Mailchimp
          </button>

          <!-- SendGrid -->
          <button
            @click="connectEmail('sendgrid')"
            class="flex items-center justify-center gap-2 rounded-lg bg-green-600 px-4 py-3 font-medium text-white transition-colors hover:bg-green-700 dark:bg-green-700 dark:hover:bg-green-800"
          >
            <i class="fa-solid fa-paper-plane"></i> SendGrid
          </button>

          <!-- Gmail/Google Workspace -->
          <button
            @click="connectEmail('gmail')"
            class="flex items-center justify-center gap-2 rounded-lg bg-red-600 px-4 py-3 font-medium text-white transition-colors hover:bg-red-700 dark:bg-red-700 dark:hover:bg-red-800"
          >
            <i class="fa-brands fa-google"></i> Gmail/Workspace
          </button>

          <!-- Amazon SES -->
          <button
            @click="connectEmail('ses')"
            class="flex items-center justify-center gap-2 rounded-lg bg-orange-600 px-4 py-3 font-medium text-white transition-colors hover:bg-orange-700 dark:bg-orange-700 dark:hover:bg-orange-800"
          >
            <i class="fa-brands fa-aws"></i> Amazon SES
          </button>

          <!-- Mailgun -->
          <button
            @click="connectEmail('mailgun')"
            class="flex items-center justify-center gap-2 rounded-lg bg-indigo-600 px-4 py-3 font-medium text-white transition-colors hover:bg-indigo-700 dark:bg-indigo-700 dark:hover:bg-indigo-800"
          >
            <i class="fa-solid fa-envelopes-bulk"></i> Mailgun
          </button>

          <!-- SMTP (Custom) -->
          <button
            @click="connectEmail('smtp')"
            class="flex items-center justify-center gap-2 rounded-lg bg-gray-600 px-4 py-3 font-medium text-white transition-colors hover:bg-gray-700 dark:bg-gray-700 dark:hover:bg-gray-800"
          >
            <i class="fa-solid fa-server"></i> Custom SMTP
          </button>
        </div>

        <!-- Additional SMTP Warning -->
        <div
          class="mt-4 bg-blue-50 border-l-4 border-blue-400 p-4 dark:bg-blue-900/20 dark:border-blue-500"
        >
          <div class="flex">
            <div class="flex-shrink-0">
              <svg
                class="h-5 w-5 text-blue-400 dark:text-blue-300"
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fill-rule="evenodd"
                  d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2h-1V9z"
                  clip-rule="evenodd"
                />
              </svg>
            </div>
            <div class="ml-3">
              <p class="text-sm text-blue-700 dark:text-blue-300">
                For SMTP services, ensure your provider allows programmatic
                access and has appropriate sending limits for your needs.
              </p>
            </div>
          </div>
        </div>
      </div>

      <div v-else class="space-y-6">
        <!-- Connection Status -->
        <div class="flex items-center justify-between">
          <div class="flex items-center text-green-600 dark:text-green-400">
            <svg class="mr-2 h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
              <path
                fill-rule="evenodd"
                d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                clip-rule="evenodd"
              />
            </svg>
            <span>Connected to {{ emailService.name }}</span>
          </div>
          <button
            @click="disconnectEmail"
            class="rounded-lg bg-red-600 px-3 py-1 text-sm text-white transition-colors hover:bg-red-700 dark:bg-red-700 dark:hover:bg-red-800"
          >
            Disconnect
          </button>
        </div>

        <!-- Service-specific warnings -->
        <div
          v-if="emailService.name.toLowerCase() === 'gmail'"
          class="bg-yellow-50 border-l-4 border-yellow-400 p-4 dark:bg-yellow-900/20 dark:border-yellow-500"
        >
          <div class="flex">
            <div class="flex-shrink-0">
              <svg
                class="h-5 w-5 text-yellow-400 dark:text-yellow-300"
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fill-rule="evenodd"
                  d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                  clip-rule="evenodd"
                />
              </svg>
            </div>
            <div class="ml-3">
              <p class="text-sm text-yellow-700 dark:text-yellow-300">
                <strong>Gmail Limitations:</strong> You're currently using Gmail
                which has strict sending limits (500 emails/day for free
                accounts). Consider upgrading to a dedicated email service for
                bulk campaigns.
              </p>
            </div>
          </div>
        </div>

        <!-- Email configuration components would go here -->
        <div class="space-y-4">
          <h4 class="font-medium text-gray-700 dark:text-gray-300">
            Email Settings
          </h4>

          <!-- Service-specific settings -->
          <div v-if="emailService.name.toLowerCase() === 'gmail'">
            <p class="text-sm text-gray-500 dark:text-gray-400 mb-4">
              Gmail configuration requires OAuth 2.0 authentication. You'll be
              redirected to Google to authorize access.
            </p>
          </div>

          <!-- Common settings -->
          <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label
                class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
                >From Name</label
              >
              <input
                type="text"
                v-model="emailSettings.fromName"
                class="w-full rounded-lg border-gray-300 dark:border-gray-600 dark:bg-gray-700"
              />
            </div>
            <div>
              <label
                class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
                >From Email</label
              >
              <input
                type="email"
                v-model="emailSettings.fromEmail"
                class="w-full rounded-lg border-gray-300 dark:border-gray-600 dark:bg-gray-700"
              />
            </div>
          </div>

          <!-- Daily sending limit warning -->
          <div
            v-if="emailService.dailyLimit"
            class="mt-2 text-sm text-gray-500 dark:text-gray-400"
          >
            <i class="fas fa-info-circle mr-1"></i>
            Your current plan allows up to
            {{ emailService.dailyLimit.toLocaleString() }} emails per day.
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
export default {
  name: "EmailMarketing",
  data() {
    return {
      emailExpanded: false,
      isEmailConnected: false,
      emailAutoPilotEnabled: false,
      emailService: {},
      emailSettings: {
        fromName: "",
        fromEmail: "",
      },
    };
  },
  computed: {
    ...mapGetters(["isEmailConnected"]),
  },
  methods: {
    connectEmail(service) {
      // Logic to connect to the selected email service
      this.isEmailConnected = true;
      this.emailService = {
        name: service.charAt(0).toUpperCase() + service.slice(1),
      }; // Example service object
    },
    disconnectEmail() {
      // Logic to disconnect the email service
      this.isEmailConnected = false;
      this.emailService = {};
    },
  },
};
</script>

<style scoped>
button,
.cursor-pointer {
  transition: all 0.2s ease;
}

/* Card hover effects */
.cursor-pointer:hover {
  transform: translateY(-1px);
}
</style>
